
import React, { useState, useMemo, useEffect } from 'react';
import { calculateFullPipeSizing, SizingInputs } from '../engine/hydraulics';
import { MATERIALS } from '../engine/pipeDatabase';
import { convertFlowToM3PerSec, FlowUnit } from '../engine/unitConversions';
import { useLanguage } from '../i18n/LanguageContext';
import { getWaterCalculatorText } from '../i18n/waterCalculatorTranslations';
import { WaterPipeReportData } from './PrintableReport';

interface WaterPipeCalculatorProps {
  unitSystem: 'metric' | 'imperial';
  onShowToast: (msg: string) => void;
  onPrint: () => void;
  onReportDataChange?: (data: WaterPipeReportData | null) => void;
}

export const WaterPipeCalculator: React.FC<WaterPipeCalculatorProps> = ({
  unitSystem,
  onShowToast,
  onPrint,
  onReportDataChange,
}) => {
  const { language } = useLanguage();
  const t = getWaterCalculatorText(language);
  const isHindi = language === 'hi';

  const [rawFlow, setRawFlow] = useState<string>('0.75');
  const [flowUnit, setFlowUnit] = useState<FlowUnit>('L/s');
  const [rawVelocity, setRawVelocity] = useState<string>('1.2');
  const [materialId, setMaterialId] = useState<string>('copper_en1057');
  const [rawLength, setRawLength] = useState<string>('25');
  const [rawTemp, setRawTemp] = useState<string>('20');

  const [elbow90, setElbow90] = useState<number>(4);
  const [tee, setTee] = useState<number>(2);
  const [valve, setValve] = useState<number>(2);
  const [showAdvanced, setShowAdvanced] = useState<boolean>(false);
  const [showProof, setShowProof] = useState<boolean>(false);

  const flowVal = parseFloat(rawFlow);
  const vVal = parseFloat(rawVelocity);
  const lenVal = parseFloat(rawLength);
  const tempVal = parseFloat(rawTemp);

  const validationError = useMemo(() => {
    if (isNaN(flowVal) || flowVal <= 0) return t.invalidFlow;
    if (isNaN(vVal) || vVal <= 0) return t.invalidVelocity;
    if (isNaN(lenVal) || lenVal <= 0) return t.invalidLength;
    if (isNaN(tempVal) || tempVal < 0 || tempVal > 100) return t.invalidTemperature;
    if (!MATERIALS[materialId]) {
      return isHindi
        ? `असमर्थित सामग्री पहचान: "${materialId}".`
        : `Unsupported material identifier: "${materialId}".`;
    }
    return null;
  }, [flowVal, vVal, lenVal, tempVal, materialId, t, isHindi]);

  const fittingKTotal = useMemo(() => {
    return Math.max(0, elbow90 * 0.75 + tee * 1.5 + valve * 0.05);
  }, [elbow90, tee, valve]);

  const calculationResult = useMemo(() => {
    if (validationError) return null;

    try {
      const flowM3s = convertFlowToM3PerSec(flowVal, flowUnit);
      const inputs: SizingInputs = {
        flowRateM3s: flowM3s,
        targetVelocityMs: unitSystem === 'imperial' ? vVal / 3.28084 : vVal,
        materialId,
        pipeLengthMeters: unitSystem === 'imperial' ? lenVal / 3.28084 : lenVal,
        temperatureC: unitSystem === 'imperial' ? ((tempVal - 32) * 5) / 9 : tempVal,
        fittingKTotal,
      };
      return calculateFullPipeSizing(inputs);
    } catch (err: any) {
      return { error: err.message || t.calculationError };
    }
  }, [
    validationError,
    flowVal,
    flowUnit,
    vVal,
    lenVal,
    tempVal,
    materialId,
    fittingKTotal,
    unitSystem,
    t,
  ]);

  useEffect(() => {
    if (!onReportDataChange) return;

    if (validationError || !calculationResult || 'error' in calculationResult) {
      onReportDataChange(null);
      return;
    }

    const selectedMaterial = MATERIALS[materialId];
    onReportDataChange({
      inputs: {
        rawFlow,
        flowUnit,
        rawVelocity,
        materialId,
        materialName: selectedMaterial?.name ?? materialId,
        roughnessMm: selectedMaterial?.roughnessMm ?? 0,
        rawLength,
        rawTemp,
        elbow90,
        tee,
        valve,
        fittingKTotal,
      },
      unitSystem,
      result: calculationResult,
    });
  }, [
    validationError,
    calculationResult,
    rawFlow,
    flowUnit,
    rawVelocity,
    materialId,
    rawLength,
    rawTemp,
    elbow90,
    tee,
    valve,
    fittingKTotal,
    unitSystem,
    onReportDataChange,
  ]);

  const getFlowRegimeLabel = (regime: string) => {
    const value = regime.toLowerCase();

    if (isHindi) {
      if (value.includes('laminar')) return 'लैमिनार';
      if (value.includes('transitional')) return 'संक्रमणकालीन';
      if (value.includes('turbulent')) return 'टर्बुलेंट';
    }

    return regime;
  };

  const handleCopy = () => {
    if (!calculationResult || 'error' in calculationResult) return;

    const res = calculationResult;
    const text = `PipeSizingCalculator.com Verification:
- Recommended Pipe: ${res.recommendedPipe.nominalSize} (${MATERIALS[materialId]?.name})
- Internal Bore: ${res.recommendedPipe.innerDiameterMm.toFixed(2)} mm (OD: ${res.recommendedPipe.outerDiameterMm.toFixed(2)} mm)
- Design Flow: ${rawFlow} ${flowUnit}
- Velocity: ${res.actualVelocityMs.toFixed(2)} m/s
- Reynolds Number: ${res.reynoldsNumber.toLocaleString('en-US')} (${res.flowRegime})
- Total Head Loss: ${res.totalHeadLossM.toFixed(2)} m head (${res.pressureDropKPa.toFixed(1)} kPa / ${res.pressureDropBar.toFixed(3)} bar)`;

    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(() => onShowToast(t.copied));
    } else {
      onShowToast(t.summaryGenerated);
    }
  };

  const handleReset = () => {
    setRawFlow('0.75');
    setFlowUnit('L/s');
    setRawVelocity('1.2');
    setMaterialId('copper_en1057');
    setRawLength('25');
    setRawTemp('20');
    setElbow90(4);
    setTee(2);
    setValve(2);
    onShowToast(t.resetDone);
  };

  const temperaturePresets = [
    {
      label: t.cold,
      metric: '10',
      imperial: '50',
      display: '10°C / 50°F',
    },
    {
      label: t.room,
      metric: '20',
      imperial: '68',
      display: '20°C / 68°F',
    },
    {
      label: t.hot,
      metric: '60',
      imperial: '140',
      display: '60°C / 140°F',
    },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">

      {/* Inputs */}
      <div className="lg:col-span-5 bg-white p-6 rounded-3xl border border-slate-200 shadow-sm space-y-5">
        <div className="flex items-center justify-between pb-3 border-b border-slate-100">
          <div className="flex items-center gap-2">
            <span className="w-8 h-8 rounded-lg bg-teal-50 text-[#087F9C] flex items-center justify-center font-bold text-sm">1</span>
            <h2 className="font-display font-bold text-lg text-[#10283D]">{t.designParameters}</h2>
          </div>
          <button
            onClick={handleReset}
            className="text-xs text-slate-400 hover:text-rose-600 flex items-center gap-1 font-medium transition-colors"
          >
            {t.reset}
          </button>
        </div>

        {validationError && (
          <div className="p-3 bg-rose-50 border border-rose-200 rounded-xl text-rose-800 text-xs font-semibold flex items-center gap-2">
            <span>⚠️</span>
            <span>{validationError}</span>
          </div>
        )}

        {/* Flow Rate */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.flowRate} <span className="text-rose-500">*</span>
          </label>
          <div className="flex rounded-xl shadow-xs">
            <input
              type="number"
              step="0.01"
              value={rawFlow}
              onChange={(e) => setRawFlow(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-l-xl border border-slate-300 focus:border-[#087F9C] focus:ring-2 focus:ring-[#087F9C]/20 outline-none text-slate-800 font-semibold text-sm"
              placeholder="0.75"
            />
            <select
              value={flowUnit}
              onChange={(e) => setFlowUnit(e.target.value as FlowUnit)}
              className="bg-slate-100 border border-l-0 border-slate-300 rounded-r-xl px-3 text-xs font-bold text-slate-700 outline-none cursor-pointer"
            >
              <option value="L/s">L/s</option>
              <option value="m3/h">m³/h</option>
              <option value="L/min">L/min</option>
              <option value="gpm_us">US GPM</option>
              <option value="gpm_uk">UK GPM</option>
            </select>
          </div>
        </div>

        {/* Target Velocity */}
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-600">
              {t.targetVelocity}
            </label>
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-md bg-teal-50 text-[#087F9C]">
              {t.recommended}: {unitSystem === 'metric' ? '1.0 - 1.5 m/s' : '3.3 - 5.0 ft/s'}
            </span>
          </div>
          <div className="relative">
            <input
              type="number"
              step="0.1"
              value={rawVelocity}
              onChange={(e) => setRawVelocity(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-[#087F9C] focus:ring-2 focus:ring-[#087F9C]/20 outline-none text-slate-800 font-semibold text-sm"
            />
            <span className="absolute right-3.5 top-2.5 text-xs font-semibold text-slate-400">
              {unitSystem === 'metric' ? 'm/s' : 'ft/s'}
            </span>
          </div>
        </div>

        {/* Pipe Material */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.pipeMaterial}
          </label>
          <select
            value={materialId}
            onChange={(e) => setMaterialId(e.target.value)}
            className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-[#087F9C] focus:ring-2 focus:ring-[#087F9C]/20 outline-none text-slate-800 font-medium text-sm bg-white cursor-pointer"
          >
            {Object.values(MATERIALS).map((m) => (
              <option key={m.id} value={m.id}>{m.name}</option>
            ))}
          </select>
          <div className="text-[11px] text-slate-500 mt-1.5 flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-teal-500"></span>
            <span>{t.roughness}: ε = {MATERIALS[materialId]?.roughnessMm} mm ({MATERIALS[materialId]?.description})</span>
          </div>
        </div>

        {/* Pipe Length */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.pipeLength}
          </label>
          <div className="relative">
            <input
              type="number"
              step="1"
              value={rawLength}
              onChange={(e) => setRawLength(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-[#087F9C] focus:ring-2 focus:ring-[#087F9C]/20 outline-none text-slate-800 font-semibold text-sm"
            />
            <span className="absolute right-3.5 top-2.5 text-xs font-semibold text-slate-400">
              {unitSystem === 'metric' ? 'm' : 'ft'}
            </span>
          </div>
        </div>

        {/* Temperature */}
        <div>
          <label className="block text-xs font-bold uppercase tracking-wider text-slate-600 mb-1">
            {t.temperature}
          </label>
          <div className="relative">
            <input
              type="number"
              step="1"
              value={rawTemp}
              onChange={(e) => setRawTemp(e.target.value)}
              className="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 focus:border-[#087F9C] focus:ring-2 focus:ring-[#087F9C]/20 outline-none text-slate-800 font-semibold text-sm"
            />
            <span className="absolute right-3.5 top-2.5 text-xs font-semibold text-slate-400">
              {unitSystem === 'metric' ? '°C' : '°F'}
            </span>
          </div>

          <div className="flex flex-wrap gap-2 mt-1.5 text-[11px]">
            {temperaturePresets.map((preset) => (
              <button
                key={preset.label}
                type="button"
                onClick={() => setRawTemp(unitSystem === 'metric' ? preset.metric : preset.imperial)}
                className="py-1 px-2.5 rounded bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium"
              >
                {preset.label} ({preset.display})
              </button>
            ))}
          </div>
        </div>

        {/* Advanced Fittings */}
        <div className="pt-2 border-t border-slate-100">
          <button
            type="button"
            onClick={() => setShowAdvanced(!showAdvanced)}
            className="w-full text-left text-xs font-bold text-slate-500 hover:text-slate-800 flex items-center justify-between py-1"
          >
            <span>
              + {t.advanced} (ΣK = {fittingKTotal.toFixed(2)})
            </span>
            <span>{showAdvanced ? '▲' : '▼'}</span>
          </button>

          {showAdvanced && (
            <div className="mt-3 p-3 bg-slate-50 rounded-xl space-y-2 border border-slate-200/70">
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{t.elbows} (K=0.75):</span>
                <input
                  type="number"
                  min="0"
                  value={elbow90}
                  onChange={(e) => setElbow90(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-16 px-2 py-1 rounded border border-slate-300 text-center font-bold"
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{t.tees} (K=1.5):</span>
                <input
                  type="number"
                  min="0"
                  value={tee}
                  onChange={(e) => setTee(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-16 px-2 py-1 rounded border border-slate-300 text-center font-bold"
                />
              </div>
              <div className="flex items-center justify-between text-xs">
                <span className="text-slate-600">{t.valves} (K=0.05):</span>
                <input
                  type="number"
                  min="0"
                  value={valve}
                  onChange={(e) => setValve(Math.max(0, parseInt(e.target.value) || 0))}
                  className="w-16 px-2 py-1 rounded border border-slate-300 text-center font-bold"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="lg:col-span-7 space-y-6">
        {calculationResult && !('error' in calculationResult) ? (
          <>
            {/* Recommended Pipe */}
            <div className="bg-gradient-to-br from-[#10283D] to-[#1E3A5F] rounded-3xl p-6 text-white shadow-xl relative overflow-hidden">
              <div className="flex items-center justify-between mb-4">
                <span className="text-xs font-bold uppercase tracking-widest text-[#39BFD3] flex items-center gap-1.5">
                  <span className="w-2 h-2 rounded-full bg-[#39BFD3] animate-ping"></span>
                  {t.recommendedSize}
                </span>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleCopy}
                    className="px-2.5 py-1 rounded-lg bg-white/10 hover:bg-white/20 text-xs font-medium text-slate-200 transition-colors flex items-center gap-1"
                  >
                    {t.copy}
                  </button>
                  <button
                    onClick={onPrint}
                    className="px-2.5 py-1 rounded-lg bg-teal-500/20 hover:bg-teal-500/30 text-xs font-semibold text-teal-300 transition-colors flex items-center gap-1"
                  >
                    {t.print}
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-baseline gap-2 sm:gap-4">
                <div className="font-display font-extrabold text-4xl sm:text-5xl tracking-tight text-white">
                  {calculationResult.recommendedPipe.nominalSize}
                </div>
                <div className="text-sm font-semibold text-teal-200">
                  {MATERIALS[materialId]?.name}
                </div>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6 pt-5 border-t border-white/10 text-xs">
                {[
                  {
                    label: t.innerDiameter,
                    value: unitSystem === 'metric'
                      ? `${calculationResult.recommendedPipe.innerDiameterMm.toFixed(2)} mm`
                      : `${(calculationResult.recommendedPipe.innerDiameterMm / 25.4).toFixed(3)} in`,
                  },
                  {
                    label: t.outerDiameter,
                    value: unitSystem === 'metric'
                      ? `${calculationResult.recommendedPipe.outerDiameterMm.toFixed(2)} mm`
                      : `${(calculationResult.recommendedPipe.outerDiameterMm / 25.4).toFixed(3)} in`,
                  },
                  {
                    label: t.wallThickness,
                    value: unitSystem === 'metric'
                      ? `${calculationResult.recommendedPipe.wallThicknessMm.toFixed(2)} mm`
                      : `${(calculationResult.recommendedPipe.wallThicknessMm / 25.4).toFixed(3)} in`,
                  },
                  {
                    label: t.requiredBore,
                    value: unitSystem === 'metric'
                      ? `${calculationResult.requiredDiameterMm.toFixed(2)} mm`
                      : `${(calculationResult.requiredDiameterMm / 25.4).toFixed(3)} in`,
                  },
                ].map((item, index) => (
                  <div key={index} className="bg-white/5 p-3 rounded-2xl">
                    <span className="text-slate-400 block text-[10px] uppercase font-bold">{item.label}</span>
                    <span className={`font-bold text-base ${index === 3 ? 'text-cyan-300' : 'text-white'}`}>
                      {item.value}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Main Metrics */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">{t.flowVelocity}</span>
                <div className="font-display font-extrabold text-2xl text-[#10283D]">
                  {unitSystem === 'metric'
                    ? `${calculationResult.actualVelocityMs.toFixed(2)} m/s`
                    : `${(calculationResult.actualVelocityMs * 3.28084).toFixed(2)} ft/s`}
                </div>
                <span className={`inline-block mt-1 text-[10px] font-bold px-2 py-0.5 rounded-full ${
                  calculationResult.actualVelocityMs > 2.4
                    ? 'bg-rose-100 text-rose-800'
                    : calculationResult.actualVelocityMs < 0.5
                    ? 'bg-amber-100 text-amber-800'
                    : 'bg-emerald-100 text-emerald-800'
                }`}>
                  {calculationResult.actualVelocityMs > 2.4
                    ? t.highVelocity
                    : calculationResult.actualVelocityMs < 0.5
                    ? t.lowVelocity
                    : t.optimalVelocity}
                </span>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">{t.pressureDrop}</span>
                <div className="font-display font-extrabold text-2xl text-[#10283D]">
                  {unitSystem === 'metric'
                    ? `${calculationResult.pressureDropKPa.toFixed(1)} kPa`
                    : `${calculationResult.pressureDropPsi.toFixed(2)} psi`}
                </div>
                <span className="text-[11px] text-slate-500 block mt-1">
                  {calculationResult.pressureDropBar.toFixed(3)} bar
                </span>
              </div>

              <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs">
                <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block mb-1">{t.totalHeadLoss}</span>
                <div className="font-display font-extrabold text-2xl text-[#10283D]">
                  {unitSystem === 'metric'
                    ? `${calculationResult.totalHeadLossM.toFixed(2)} m`
                    : `${(calculationResult.totalHeadLossM * 3.28084).toFixed(2)} ft`}
                </div>
                <span className="text-[11px] text-[#087F9C] font-semibold block mt-1">
                  {calculationResult.hydraulicGradientKPaPer100m.toFixed(1)} kPa/100m
                </span>
              </div>
            </div>

            {/* Fluid Mechanics */}
            <div className="bg-white p-5 rounded-2xl border border-slate-200 shadow-xs space-y-3">
              <h3 className="font-display font-bold text-sm text-[#10283D]">{t.fluidMechanics}</h3>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">{t.reynolds}</span>
                  <span className="font-mono font-bold text-slate-800 text-sm">
                    {calculationResult.reynoldsNumber.toLocaleString('en-US')}
                  </span>
                  <span className="block text-[10px] font-semibold text-emerald-600 mt-0.5">
                    {getFlowRegimeLabel(calculationResult.flowRegime)} Flow
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">{t.frictionFactor} (f)</span>
                  <span className="font-mono font-bold text-slate-800 text-sm">
                    {calculationResult.frictionFactor.toFixed(4)}
                  </span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">Swamee-Jain</span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">{t.waterDensity} (ρ)</span>
                  <span className="font-mono font-bold text-slate-800 text-sm">
                    {calculationResult.fluidProperties.density.toFixed(1)} kg/m³
                  </span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">
                    @ {calculationResult.fluidProperties.temperatureC}°C
                  </span>
                </div>
                <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-100">
                  <span className="text-slate-400 block text-[10px] uppercase font-bold">{t.kinematicViscosity}</span>
                  <span className="font-mono font-bold text-slate-800 text-sm">
                    {calculationResult.fluidProperties.kinematicViscosity.toExponential(3)}
                  </span>
                  <span className="block text-[10px] text-slate-400 mt-0.5">m²/s</span>
                </div>
              </div>

              {/* Engineering warnings from calculation engine remain unchanged */}
              {calculationResult.warnings.length > 0 && (
                <div className="space-y-2 pt-2">
                  {calculationResult.warnings.map((w, idx) => (
                    <div key={idx} className="p-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-900 text-xs flex items-start gap-2">
                      <span className="text-amber-600 font-bold">⚠️</span>
                      <span>{w}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Mathematical Working */}
            <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-xs">
              <button
                type="button"
                onClick={() => setShowProof(!showProof)}
                className="w-full p-4 text-left font-display font-bold text-sm text-[#10283D] flex items-center justify-between hover:bg-slate-50 transition-colors"
              >
                <span>{t.fullWorking}</span>
                <span>{showProof ? '▲' : '▼'}</span>
              </button>

              {showProof && (
                <div className="p-5 border-t border-slate-100 bg-slate-50/70 font-mono text-xs space-y-3 text-slate-700">
                  <div>
                    <span className="font-bold text-slate-900 block font-sans">1. {t.theoreticalDiameter}:</span>
                    <div className="mt-1 p-2 bg-white rounded border border-slate-200">
                      {calculationResult.calculationsExplanation.continuityFormula}
                    </div>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block font-sans">2. {t.actualVelocity}:</span>
                    <div className="mt-1 p-2 bg-white rounded border border-slate-200">
                      {calculationResult.calculationsExplanation.velocityCheck}
                    </div>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block font-sans">3. {t.reynoldsCheck}:</span>
                    <div className="mt-1 p-2 bg-white rounded border border-slate-200">
                      {calculationResult.calculationsExplanation.reynoldsFormula}
                    </div>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block font-sans">4. {t.darcyFactor}:</span>
                    <div className="mt-1 p-2 bg-white rounded border border-slate-200">
                      {calculationResult.calculationsExplanation.frictionFormula}
                    </div>
                  </div>
                  <div>
                    <span className="font-bold text-slate-900 block font-sans">5. {t.darcyPressure}:</span>
                    <div className="mt-1 p-2 bg-white rounded border border-slate-200">
                      {calculationResult.calculationsExplanation.darcyFormula}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Dedicated PDF Report Button below calculation results */}
            <div className="pt-2 flex flex-wrap items-center justify-between gap-3">
              <span className="text-xs text-slate-500 font-medium">
                Generate verified BS EN 806 pipe sizing &amp; hydraulic verification sheet
              </span>
              <button
                type="button"
                onClick={onPrint}
                className="py-2.5 px-5 rounded-2xl font-bold text-xs bg-[#10283D] hover:bg-[#1E3A5F] active:bg-[#087F9C] text-white shadow-md flex items-center gap-2 transition-all duration-200 border border-slate-700/30"
              >
                <svg
                  className="w-4 h-4 text-[#39BFD3]"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth="2"
                  aria-hidden="true"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"
                  />
                </svg>
                <span>{t.print}</span>
              </button>
            </div>
          </>
        ) : (
          <div className="bg-white p-8 rounded-3xl border border-slate-200 text-center text-slate-500">
            {t.validParameters}
          </div>
        )}
      </div>
    </div>
  );
};
